import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timelinePlugin from "@fullcalendar/timeline";
import { addDays, format, subDays } from "date-fns";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  memo,
} from "react";
import { useNavigate } from "react-router-dom";

interface CalendarInterface {
  filteredData?: any;
  setCurrentView?: any;
  setCalendarDate?: any;
  startDate?: any;
  calendarDate?: any;
  setStartDateCalendar?: any;
  setEndDateCalendar?: any;
  setStartDateCalendarDay?: any;
  currentView?: any;
}

/**
 * Utility to get timezone offset and handle date calculations
 */
const getTimezoneInfo = () => {
  const offset = new Date().getTimezoneOffset();
  return {
    offset,
    isNegative: offset > 0,
  };
};

/**
 * Calculate end date based on view type and timezone
 */
const calculateEndDate = (endDate: any, isMonthOrWeekView: boolean): string => {
  const { isNegative } = getTimezoneInfo();
  if (isMonthOrWeekView) {
    return format(subDays(new Date(endDate), isNegative ? 0 : 1), "yyyy-MM-dd");
  }
  return format(new Date(endDate), "yyyy-MM-dd");
};

/**
 * Determine if an event is a task, meeting, or goal based on available fields
 */
const getEventType = (event: any): "task" | "meeting" | "goal" => {
  // Check if it's explicitly marked as a goal
  if (event.event_type === "goal") {
    return "goal";
  }
  // Tasks have assigned_to_id, meetings don't
  return event.assigned_to_id ? "task" : "meeting";
};

/**
 * Memoized Event Content Component - handles meetings, tasks, and goals
 */
const EventContent = memo(({ eventInfo }: { eventInfo: any }) => {
  const navigate = useNavigate();
  const { start, end, title, id } = eventInfo?.event;
  const event =
    eventInfo?.event?.extendedProps?.originalEvent || eventInfo?.event;
  const eventType = getEventType(event);

  const handleEventClick = useCallback(() => {
    const formattedStart = format(
      new Date(start),
      "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
    );
    const formattedEnd = format(new Date(end), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");

    if (eventType === "task") {
      // Navigate to task detail page
      navigate("/task_detail", {
        state: {
          taskId: id,
        },
      });
    } else if (eventType === "goal") {
      // Navigate to goal detail page
      navigate("/goal_view", {
        state: {
          goalID: id,
          projectName: event?.project_name
        },
      });
    } else {
      // Navigate to meeting detail page
      navigate("/meeting_detail", {
        state: {
          meetingID: id,
          startMeetDate: formattedStart,
          endMeetDate: formattedEnd,
        },
      });
    }
  }, [navigate, id, start, end, eventType, event]);

  const startTime = useMemo(() => format(new Date(start), "hh:mm a"), [start]);
  const endTime = useMemo(() => format(new Date(end), "hh:mm a"), [end]);
  const startDate = useMemo(() => format(new Date(start), "MM/dd/yyyy"), [start]);
  const endDate = useMemo(() => format(new Date(end), "MM/dd/yyyy"), [end]);

  // Get status badge for tasks
  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: string } = {
      "Production Release": "prod-rel-class",
      "Not Started": "notstarted-class",
      "In Progress": "inprogress-class",
      "Closed": "closed-class",
      "Testing": "testing-class"
    };
    return statusMap[status] || "notstarted-class";
  };

  return (
    <div
      className={`p-2 cursor-pointer rounded border-l-4 ${(eventType === "task" || eventType === "goal") && getStatusBadge(event.status_name || event.current_status_name)
        } meeting_card_box_dv event-card`}
      onClick={handleEventClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleEventClick();
        }
      }}
    >
      {/* Task-specific information */}
      {eventType === "task" && (
        <>
          <div className="grid items-start justify-between gap-0">
            <div className="flex-1 min-w-0">
              <p className="font-semibold my-0 text-sm truncate">
                Task : {title}
              </p>
              {event.goal_name && (
                <p className="text-xs text-gray-600 my-0">
                  Goal: {event.goal_name}
                </p>
              )}
              <div>
                {event.project_name && (
                  <p className="text-xs text-gray-600 my-0">
                    Project: {event.project_name}
                  </p>
                )}
                <p className={`text-xs my-0  w-[390px] flex `}>
                  Duration : {`${event?.hours} Hours ${event?.mins} Minutes`}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Goal-specific information */}
      {eventType === "goal" && (
        <>
          <div className="grid items-start justify-between gap-0">
            <div className="flex-1 min-w-0">
              <p className="font-semibold my-0 text-sm truncate">
                Goal : {title}
              </p>
              {event.goal_members && event.goal_members.length > 0 && (
                <p
                  title={event.goal_members.map((m: any) => m.member_name).join(", ")}
                  className="text-xs text-gray-600 my-0">
                  Members: {event.goal_members?.length}
                </p>
              )}
              {event.project_name && (
                <p className="text-xs text-gray-600 my-0">
                  Project: {event.project_name}
                </p>
              )}
              <p className={`text-xs my-0  w-[390px] flex `}>
                Date : {`${startDate} - ${endDate}`}
              </p>

            </div>
          </div>
        </>
      )}

      {/* Meeting-specific information */}
      {eventType === "meeting" && (
        <>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 grid min-w-0">
              {
                event?.is_email_send && <span className="flex items-center gap-1"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#22C55E" />
                  <path
                    d="M7 12.5L10.2 15.5L17 8.5"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg> <span className="text-sm font-semibold dark:text-white">Completed</span></span>
              }
              <p className="font-semibold my-0 text-sm truncate">Meeting : {title}</p>
              <p className="text-xs text-gray-600 my-0">
                Type: {event?.meeting_type_name}
              </p>
              <p className={`text-xs my-0  w-[390px] flex `}>
                Time : {`${startTime} - ${endTime}`}
              </p>
              {event.host_name && (
                <p className="text-xs text-gray-600 my-0 w-[390px]">
                  Host: {event.host_name}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
});

EventContent.displayName = "EventContent";

const Calendar: FunctionComponent<CalendarInterface> = ({
  filteredData,
  setCurrentView,
  setCalendarDate,
  startDate,
  calendarDate,
  setEndDateCalendar,
  setStartDateCalendar,
  setStartDateCalendarDay,
  currentView,
}) => {
  const calendarApiRef = useRef<any>(null);
  const manageTimeRef = useRef(false);
  const navigate = useNavigate();

  /**
   * Map filtered data (meetings, tasks, and goals) to FullCalendar event format
   */
  const events = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return [];
    }

    return filteredData.map((item: any) => {
      // Determine if this is a task, meeting, or goal
      const isTask = item.assigned_to_id;
      const isGoal = item.event_type === "goal";
      const eventType = isGoal ? "goal" : isTask ? "task" : "meeting";

      console.log(item, 'item')

      return {
        id: item.id,
        title: item.title,
        start: item.start_datetime,
        end: item.end_datetime,
        classNames: `event-${eventType}`,
        extendedProps: {
          eventType,
          originalEvent: item,
          // Meeting-specific props
          meetingLink: item.meeting_link_string,
          host: item.host_name,
          status: item.status_name,
          members: item.members,
          agendas: item.agendas,
          is_email_send: item.is_email_send,
          // Task-specific props
          taskId: item.id,
          assigned_to_id: item.assigned_to_id,
          assigned_to_name: item.assigned_to_name,
          priority: item.priority,
          current_status_id: item.current_status_id,
          current_status_name: item.current_status_name,
          goal_name: item.goal_name,
          goal_id: item.goal_id,
          project_name: item.project_name,
          project_id: item.project_id,
          days: item.days,
          hours: item.hours,
          mins: item.mins,
          // Goal-specific props
          goalId: item.id,
          goal_members: item.goal_members,
          goal_static_outcome: item.goal_static_outcome,
          status_id: item.status_id,
          status_name: item.status_name,
          created_by: item.created_by,
          actual_start_date: item.actual_start_date,
          actual_end_date: item.actual_end_date,
        },
      };
    });
  }, [filteredData]);

  /**
   * Navigate calendar to start date when it changes
   */
  useEffect(() => {
    if (calendarApiRef.current && startDate) {
      calendarApiRef.current.gotoDate(startDate);
    }
  }, [startDate]);

  /**
   * Handle time updates after calendar navigation
   */
  useEffect(() => {
    if (manageTimeRef.current && calendarDate) {
      const { start, end } = calendarDate;
      const currentViewCheckMonth = currentView === "dayGridMonth";
      const currentViewCheckWeek = currentView === "timelineWeek";

      const endDate = calculateEndDate(
        end,
        currentViewCheckMonth || currentViewCheckWeek,
      );

      if (currentViewCheckMonth || currentViewCheckWeek) {
        setEndDateCalendar(endDate);
        setStartDateCalendar(start);
        setStartDateCalendarDay(start);
      } else {
        setStartDateCalendar(start);
        setEndDateCalendar(start);
        setStartDateCalendarDay(start);
      }

      manageTimeRef.current = false;
    }
  }, [
    manageTimeRef.current,
    calendarDate,
    currentView,
    setStartDateCalendar,
    setEndDateCalendar,
    setStartDateCalendarDay,
  ]);

  /**
   * Handle calendar navigation (prev/next buttons)
   */
  const handlePrevNextClick = useCallback(() => {
    if (!calendarApiRef.current) return;

    manageTimeRef.current = false;
    const { currentStart, currentEnd } = calendarApiRef.current.view;

    setCalendarDate({
      start: format(new Date(currentStart), "yyyy-MM-dd"),
      end: format(new Date(currentEnd), "yyyy-MM-dd"),
    });

    manageTimeRef.current = true;
  }, [setCalendarDate]);

  /**
   * Manage event listeners for calendar navigation buttons
   */
  const manageButtonListeners = useCallback(
    (action: "add" | "remove") => {
      const prevButton = document.querySelector(".fc-prev-button");
      const nextButton = document.querySelector(".fc-next-button");

      if (prevButton) {
        prevButton[`${action}EventListener`]("click", handlePrevNextClick);
      }
      if (nextButton) {
        nextButton[`${action}EventListener`]("click", handlePrevNextClick);
      }
    },
    [handlePrevNextClick],
  );

  /**
   * Set up calendar ref and attach event listeners
   */
  const calendarRef = useCallback(
    (node: any) => {
      if (!node) return;

      const calendarApi = node.getApi();
      calendarApiRef.current = calendarApi;
      manageButtonListeners("add");

      return () => manageButtonListeners("remove");
    },
    [manageButtonListeners],
  );

  /**
   * Handle when calendar dates range changes
   */
  const handleDatesSet = useCallback(
    (arg: any) => {
      const currentDateStart = format(
        new Date(arg.view.currentStart),
        "yyyy-MM-dd",
      );
      const currentDateEnd = format(
        new Date(arg.view.currentEnd),
        "yyyy-MM-dd",
      );
      const endDate = calculateEndDate(currentDateEnd, true);

      setCalendarDate({
        start: currentDateStart,
        end: endDate,
      });

      setCurrentView(arg.view.type);
    },
    [setCalendarDate, setCurrentView],
  );

  /**
   * Render event with memoized component
   */
  const renderEventContent = useCallback((eventInfo: any) => {
    return <EventContent eventInfo={eventInfo} />;
  }, []);

  /**
   * FullCalendar view configurations
   */
  const viewConfigs = useMemo(
    () => ({
      timelineWeek: {
        slotDuration: { days: 1 },
        slotLabelFormat: function (args: any) {
          const { isNegative } = getTimezoneInfo();
          let date: any;

          if (isNegative) {
            date = addDays(new Date(args.date.marker), 1);
          } else {
            date = new Date(args.date.marker);
          }

          const dayName = date.toLocaleDateString("en-US", {
            weekday: "short",
          });
          const dayNumber = date.toLocaleDateString("en-US", {
            day: "numeric",
          });
          return `${dayName} ${dayNumber}`;
        },
        slotLabelInterval: { days: 1 },
      },
      timelineDay: {
        slotDuration: "02:00:00",
        slotLabelInterval: "02:00",
      },
    }),
    [],
  );

  return (
    <>
      <div className="w-full">
        <FullCalendar
          datesSet={handleDatesSet}
          firstDay={1}
          ref={calendarRef}
          initialDate={startDate}
          plugins={[dayGridPlugin, timelinePlugin, interactionPlugin]}
          headerToolbar={{
            left: "prev,next",
            center: "title",
            right: "dayGridMonth,timelineWeek,timelineDay",
          }}
          views={viewConfigs}
          eventContent={renderEventContent}
          slotMinWidth={60}
          editable={false}
          selectable={false}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          initialView={currentView}
          events={events}
          height="auto"
        />
      </div>

    </>
  );
};

export default Calendar;
